const csrfToken = () => document.querySelector("meta[name='csrf-token']")?.content

const postJSON = async (url) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json"
  }
  const token = csrfToken()
  if (token) headers["X-CSRF-Token"] = token

  const response = await fetch(url, {
    method: "POST",
    headers,
    credentials: "same-origin",
    body: "{}"
  })

  const contentType = response.headers.get("content-type") || ""
  const payload = contentType.includes("application/json") ? await response.json() : null

  if (!response.ok || !payload) {
    const error = new Error(payload?.error || "Request failed")
    error.status = response.status
    throw error
  }

  return payload
}

class ScratchExperience {
  static threshold = 0.45

  constructor(root) {
    this.root = root
    this.canvas = root.querySelector("[data-scratch-canvas]")
    this.progressBar = root.querySelector("[data-progress-bar]")
    this.progressTrack = root.querySelector("[data-progress-track]")
    this.progressLabel = root.querySelector("[data-progress-label]")
    this.progressArea = root.querySelector("[data-scratch-progress-area]")
    this.errorPanel = root.querySelector("[data-scratch-error]")
    this.status = root.querySelector("[data-scratch-status]")
    this.winnerMessage = root.querySelector("[data-winner-message]")
    this.card = root.querySelector("[data-scratch-card]")
    this.flash = root.querySelector("[data-win-flash]")
    this.confettiCanvas = root.querySelector("[data-confetti-canvas]")
    this.revealUrl = root.dataset.revealUrl
    this.resultUrl = root.dataset.resultUrl
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.drawing = false
    this.busy = false
    this.revealed = false
    this.moveCount = 0
    this.lastPoint = null

    this.prepareFoil()
    this.watchSize()
    this.bindEvents()
  }

  prepareFoil() {
    const width = this.canvas.clientWidth
    const height = this.canvas.clientHeight
    if (!width || !height) return

    this.dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.canvas.width = Math.round(width * this.dpr)
    this.canvas.height = Math.round(height * this.dpr)
    this.foilWidth = width
    this.foilHeight = height
    this.ctx = this.canvas.getContext("2d", { willReadFrequently: true })
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)

    const gradient = this.ctx.createLinearGradient(0, 0, width, height)
    gradient.addColorStop(0, "#e9d9a6")
    gradient.addColorStop(0.3, "#c2a25c")
    gradient.addColorStop(0.55, "#eedfad")
    gradient.addColorStop(0.8, "#b3924e")
    gradient.addColorStop(1, "#d9c288")
    this.ctx.fillStyle = gradient
    this.ctx.fillRect(0, 0, width, height)

    this.ctx.save()
    this.ctx.globalAlpha = 0.13
    this.ctx.fillStyle = "#fff"
    for (let x = -height; x < width + height; x += 26) {
      this.ctx.beginPath()
      this.ctx.moveTo(x, 0)
      this.ctx.lineTo(x + 9, 0)
      this.ctx.lineTo(x + 9 - height, height)
      this.ctx.lineTo(x - height, height)
      this.ctx.closePath()
      this.ctx.fill()
    }
    this.ctx.restore()

    this.ctx.textAlign = "center"
    this.ctx.textBaseline = "middle"
    this.ctx.fillStyle = "rgba(58, 42, 12, 0.8)"
    this.ctx.font = "700 15px Cinzel, serif"
    this.ctx.fillText("S C R A T C H", width / 2, height / 2 - 9)
    this.ctx.font = '500 10px "Noto Sans JP", sans-serif'
    this.ctx.fillText("指 で 削 っ て く だ さ い", width / 2, height / 2 + 12)
  }

  bindEvents() {
    this.canvas.addEventListener("pointerdown", (event) => this.pointerDown(event))
    this.canvas.addEventListener("pointermove", (event) => this.pointerMove(event))
    this.canvas.addEventListener("pointerup", () => this.pointerUp())
    this.canvas.addEventListener("pointercancel", () => this.pointerUp())
    this.canvas.addEventListener("lostpointercapture", () => this.pointerUp())
    this.canvas.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return
      event.preventDefault()
      this.requestReveal()
    })

    this.root.querySelector("[data-keyboard-reveal]")?.addEventListener("click", () => this.requestReveal())
    this.root.querySelector("[data-scratch-retry]")?.addEventListener("click", () => this.requestReveal())
  }

  // 箔は起動時の寸法で焼くが、その後に版面が動くことがある（Webフォントの
  // 読み込みが主。0幅で焼けずに ctx を持てないまま始まる場合もある）。まだ誰も
  // 削っていない間だけ焼き直して、実寸に合わせる。削り始めた後は削り跡が消えて
  // しまうので焼き直さない。座標側は scratchAt が焼いた寸法基準で写すので、
  // 焼き直せない場合でもズレは出ない。
  watchSize() {
    if (typeof ResizeObserver === "undefined") return

    this.sizeObserver = new ResizeObserver(() => {
      if (this.revealed || this.busy || this.moveCount > 0) return
      if (this.canvas.clientWidth === this.foilWidth && this.canvas.clientHeight === this.foilHeight) return

      this.prepareFoil()
    })
    this.sizeObserver.observe(this.canvas)
  }

  pointerDown(event) {
    if (this.revealed || this.busy || !this.ctx) return
    event.preventDefault()
    try { this.canvas.setPointerCapture(event.pointerId) } catch (_) { /* Older browsers */ }
    this.drawing = true
    this.lastPoint = null
    this.scratchAt(event)
  }

  pointerMove(event) {
    if (!this.drawing || this.revealed || this.busy) return
    event.preventDefault()
    this.scratchAt(event)
  }

  pointerUp() {
    if (!this.drawing) return
    this.drawing = false
    this.lastPoint = null
    this.checkRatio()
  }

  scratchAt(event) {
    if (!this.ctx) return

    const rect = this.canvas.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    // 箔を焼いた時点の座標系へ写す。表示幅が焼いた後に変わっていると、絵だけが
    // 引き伸ばされて座標がついてこない。clientWidth（今の幅）で割ると等倍のまま
    // 計算してしまい、左端では合うのに右へ行くほど指から離れていく。
    const scaleX = this.canvas.width / this.dpr / rect.width
    const scaleY = this.canvas.height / this.dpr / rect.height
    const x = (event.clientX - rect.left) * scaleX
    const y = (event.clientY - rect.top) * scaleY

    this.ctx.globalCompositeOperation = "destination-out"
    this.ctx.strokeStyle = "#000"
    this.ctx.fillStyle = "#000"
    this.ctx.lineWidth = 34
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
    this.ctx.beginPath()
    this.ctx.moveTo(this.lastPoint?.x ?? x, this.lastPoint?.y ?? y)
    this.ctx.lineTo(x, y)
    this.ctx.stroke()
    this.ctx.beginPath()
    this.ctx.arc(x, y, 17, 0, Math.PI * 2)
    this.ctx.fill()
    this.ctx.globalCompositeOperation = "source-over"

    this.lastPoint = { x, y }
    this.moveCount += 1
    if (this.moveCount % 4 === 0) this.checkRatio()
  }

  checkRatio() {
    if (this.revealed || this.busy || !this.ctx) return

    const { width, height } = this.canvas
    const pixels = this.ctx.getImageData(0, 0, width, height).data
    const sampleStep = Math.max(8, Math.round(10 * this.dpr))
    let clear = 0
    let total = 0

    for (let y = Math.floor(sampleStep / 2); y < height; y += sampleStep) {
      for (let x = Math.floor(sampleStep / 2); x < width; x += sampleStep) {
        const alpha = pixels[((y * width + x) * 4) + 3]
        total += 1
        if (alpha < 120) clear += 1
      }
    }

    const ratio = total ? clear / total : 0
    this.updateProgress(ratio)
    if (ratio >= ScratchExperience.threshold) this.requestReveal()
  }

  updateProgress(ratio) {
    const percentage = Math.min(100, Math.round((ratio / ScratchExperience.threshold) * 100))
    this.progressBar.style.width = `${percentage}%`
    this.progressTrack.setAttribute("aria-valuenow", String(percentage))
    this.progressLabel.textContent = `開封まで ${percentage}%`
  }

  async requestReveal() {
    if (this.busy || this.revealed) return
    this.busy = true
    this.drawing = false
    this.updateProgress(ScratchExperience.threshold)
    this.errorPanel.hidden = true
    this.canvas.classList.add("n9-is-busy")
    this.canvas.setAttribute("aria-disabled", "true")
    this.progressLabel.textContent = "当選結果を確認中…"
    this.status.textContent = "当選結果を確認しています。"

    try {
      const payload = await postJSON(this.revealUrl)
      if (!payload.prize?.title || !payload.prize?.rank) throw new Error("Invalid reveal response")
      this.populatePrize(payload.prize)
      this.finishReveal(payload.prize)
    } catch (_) {
      this.busy = false
      this.canvas.classList.remove("n9-is-busy")
      this.canvas.removeAttribute("aria-disabled")
      this.progressLabel.textContent = "開封まで 100%"
      this.errorPanel.hidden = false
      this.status.textContent = "開封できませんでした。もう一度お試しください。"
      this.errorPanel.querySelector("button")?.focus()
    }
  }

  populatePrize(prize) {
    this.root.querySelector("[data-n9-prize-title]").textContent = prize.title
    this.root.querySelector("[data-n9-prize-rank]").textContent = prize.rank
    this.root.querySelector("[data-winner-rank]").textContent = prize.rank
  }

  finishReveal(prize) {
    this.revealed = true
    this.busy = false
    this.updateProgress(ScratchExperience.threshold)
    this.canvas.classList.remove("n9-is-busy")
    this.canvas.classList.add("n9-is-revealed")
    this.canvas.setAttribute("aria-label", `${prize.rank}賞、${prize.title}`)
    this.progressArea.hidden = true
    this.winnerMessage.hidden = false
    this.status.textContent = `おめでとうございます。${prize.rank}賞、${prize.title}が当選しました。`

    this.card.classList.add("n9-is-winning")
    if (!this.reducedMotion) {
      this.flash.classList.remove("n9-is-active")
      void this.flash.offsetWidth
      this.flash.classList.add("n9-is-active")
      this.startConfetti()
    }

    const delay = this.reducedMotion ? 350 : 1900
    window.setTimeout(() => window.location.assign(this.resultUrl), delay)
  }

  startConfetti() {
    const canvas = this.confettiCanvas
    const context = canvas.getContext("2d")
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const width = window.innerWidth
    const height = window.innerHeight
    canvas.width = Math.round(width * dpr)
    canvas.height = Math.round(height * dpr)
    context.setTransform(dpr, 0, 0, dpr, 0, 0)

    const cardRect = this.card.getBoundingClientRect()
    const origin = { x: cardRect.left + cardRect.width / 2, y: cardRect.top + cardRect.height / 2 }
    const colors = ["#f6e3a3", "#d4af6a", "#ffffff", "#c94f4f", "#8a63c9", "#e8d5a0"]
    const particles = []
    const random = (min, max) => min + Math.random() * (max - min)

    const burst = (count, maxSpeed) => {
      for (let index = 0; index < count; index += 1) {
        const angle = random(-Math.PI, Math.PI)
        const speed = random(2, maxSpeed)
        particles.push({
          x: origin.x,
          y: origin.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - random(2, 5),
          rotation: random(0, Math.PI * 2),
          rotationSpeed: random(-0.25, 0.25),
          width: random(4, 8),
          height: random(8, 14),
          color: colors[Math.floor(random(0, colors.length))],
          life: 1.3,
          decay: random(0.004, 0.009)
        })
      }
    }

    let frame = null
    const draw = () => {
      context.clearRect(0, 0, width, height)

      for (let index = particles.length - 1; index >= 0; index -= 1) {
        const particle = particles[index]
        particle.vy += 0.14
        particle.x += particle.vx
        particle.y += particle.vy
        particle.rotation += particle.rotationSpeed
        particle.life -= particle.decay

        if (particle.life <= 0 || particle.y > height + 30) {
          particles.splice(index, 1)
          continue
        }

        context.save()
        context.translate(particle.x, particle.y)
        context.rotate(particle.rotation)
        context.globalAlpha = Math.max(0, Math.min(1, particle.life))
        context.fillStyle = particle.color
        context.fillRect(-particle.width / 2, -particle.height / 2, particle.width, particle.height)
        context.restore()
      }

      if (particles.length) frame = window.requestAnimationFrame(draw)
      else context.clearRect(0, 0, width, height)
    }

    burst(130, 9)
    draw()
    window.setTimeout(() => {
      burst(60, 6)
      if (!frame) draw()
    }, 350)
  }
}

class CouponExperience {
  constructor(root) {
    this.root = root
    this.modal = root.querySelector("[data-use-modal]")
    this.dialog = this.modal?.querySelector(".n9-modal__dialog")
    this.openButton = root.querySelector("[data-open-use-modal]")
    this.confirmButton = root.querySelector("[data-confirm-use]")
    this.error = root.querySelector("[data-use-error]")
    this.unusedPanel = root.querySelector("[data-unused-coupon]")
    this.usedPanel = root.querySelector("[data-used-coupon]")
    this.usedAt = root.querySelector("[data-used-at]")
    this.clock = root.querySelector("[data-current-clock]")
    this.timeZone = root.dataset.timeZone || "Asia/Seoul"
    this.syncServerClock(root.dataset.serverTime)
    this.busy = false
    this.previousFocus = null
    this.inertElements = []

    this.bindEvents()
    if (this.usedPanel && !this.usedPanel.hidden) this.startClock()
  }

  bindEvents() {
    this.openButton?.addEventListener("click", () => this.openModal())
    this.modal?.querySelectorAll("[data-close-use-modal]").forEach((button) => {
      button.addEventListener("click", () => this.closeModal())
    })
    this.confirmButton?.addEventListener("click", () => this.confirmUse())
    this.modal?.addEventListener("keydown", (event) => this.handleModalKeydown(event))
  }

  openModal() {
    if (!this.modal || this.busy) return
    this.previousFocus = document.activeElement
    this.error.hidden = true
    this.modal.hidden = false
    this.inertElements = Array.from(this.root.children).filter((element) => element !== this.modal)
    this.inertElements.forEach((element) => { element.inert = true })
    document.body.style.overflow = "hidden"
    window.requestAnimationFrame(() => this.confirmButton.focus())
  }

  closeModal() {
    if (!this.modal || this.busy) return
    this.modal.hidden = true
    this.inertElements.forEach((element) => { element.inert = false })
    this.inertElements = []
    document.body.style.overflow = ""
    this.previousFocus?.focus()
  }

  handleModalKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeModal()
      return
    }
    if (event.key !== "Tab") return

    const focusable = Array.from(this.dialog.querySelectorAll("button:not(:disabled), [href], [tabindex]:not([tabindex='-1'])"))
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  async confirmUse() {
    if (this.busy) return
    this.busy = true
    this.error.hidden = true
    const originalLabel = this.confirmButton.textContent
    this.confirmButton.disabled = true
    this.confirmButton.textContent = "使用を確定しています…"

    try {
      const payload = await postJSON(this.root.dataset.useUrl)
      this.usedAt.textContent = payload.used_at_text || this.formatUsedAt(payload.used_at)
      if (payload.used_at) this.usedAt.dateTime = payload.used_at
      if (payload.server_time) this.syncServerClock(payload.server_time)
      this.modal.hidden = true
      this.inertElements.forEach((element) => { element.inert = false })
      this.inertElements = []
      document.body.style.overflow = ""
      this.unusedPanel.hidden = true
      this.usedPanel.hidden = false
      this.usedPanel.tabIndex = -1
      this.startClock()
      this.usedPanel.focus()
    } catch (_) {
      this.busy = false
      this.confirmButton.disabled = false
      this.confirmButton.textContent = originalLabel
      this.error.hidden = false
      this.error.focus?.()
    }
  }

  formatUsedAt(value) {
    if (!value) return ""
    const date = new Date(value)
    const parts = new Intl.DateTimeFormat("ja-JP", {
      timeZone: this.timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(date)
    const valueFor = (type) => parts.find((part) => part.type === type)?.value || ""
    return `${valueFor("year")}.${valueFor("month")}.${valueFor("day")} ${valueFor("hour")}:${valueFor("minute")}`
  }

  startClock() {
    if (!this.clock) return
    const formatter = new Intl.DateTimeFormat("ja-JP", {
      timeZone: this.timeZone,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23"
    })
    const update = () => {
      const elapsed = window.performance.now() - this.clockStartedAt
      this.clock.textContent = formatter.format(new Date(this.serverTime + elapsed))
    }
    update()
    if (this.clockTimer) window.clearInterval(this.clockTimer)
    this.clockTimer = window.setInterval(update, 1000)
  }

  syncServerClock(value) {
    const parsed = Date.parse(value)
    this.serverTime = Number.isNaN(parsed) ? Date.now() : parsed
    this.clockStartedAt = window.performance.now()
  }
}

const bootN9CustomerUI = () => {
  document.querySelectorAll("[data-n9-scratch]").forEach((root) => {
    if (root.dataset.n9Enhanced) return
    root.dataset.n9Enhanced = "true"
    new ScratchExperience(root)
  })
  document.querySelectorAll("[data-n9-coupon]").forEach((root) => {
    if (root.dataset.n9Enhanced) return
    root.dataset.n9Enhanced = "true"
    new CouponExperience(root)
  })
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootN9CustomerUI, { once: true })
} else {
  bootN9CustomerUI()
}

// アプリ内ブラウザ案内のURLコピー。標準ブラウザで開き直してもらうための補助。
document.addEventListener("click", async (event) => {
  const button = event.target.closest("[data-in-app-browser-copy]")
  if (!button) return

  const field = document.querySelector("[data-in-app-browser-url]")
  if (!field) return

  try {
    await navigator.clipboard.writeText(field.value)
  } catch {
    field.select()
    field.setSelectionRange(0, field.value.length)
    document.execCommand("copy")
  }
  button.textContent = "コピーしました"
})

// 通知は数秒で自動的に引っ込める。タップでも即座に消せる。
// 画面上部に残り続けると操作の邪魔になるため。
const dismissFlash = (element) => {
  if (element.dataset.dismissing) return
  element.dataset.dismissing = "true"
  element.classList.add("is-dismissed")
  // アニメーションの終了を待って高さごと畳む。transition が効かない環境でも
  // タイマーで確実に消えるようにしておく。
  const remove = () => element.remove()
  element.addEventListener("transitionend", remove, { once: true })
  window.setTimeout(remove, 600)
}

const initAutoDismiss = () => {
  document.querySelectorAll("[data-auto-dismiss]").forEach((element) => {
    window.setTimeout(() => dismissFlash(element), 4000)
  })
}

document.addEventListener("click", (event) => {
  const flash = event.target.closest("[data-auto-dismiss]")
  if (flash) dismissFlash(flash)
})

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAutoDismiss, { once: true })
} else {
  initAutoDismiss()
}
